import React, { useState } from 'react';
import { Window } from '../Window/Window';
import styled from 'styled-components';
import { Chessboard } from 'react-chessboard';
import { Chess } from 'chess.js';
import { customPieces } from './ChessPieces';

const ChessContainer = styled.div`
  background: #d4d0c8;
  padding: 20px;
  height: calc(100% - 30px);
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 16px;
  }
  
  &::-webkit-scrollbar-track {
    background: #c0c0c0;
    border-left: 1px solid #808080;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #808080;
    border: 1px solid #c0c0c0;
    border-right: 1px solid #000;
    border-bottom: 1px solid #000;
  }
`;

const GameContent = styled.div`
  display: ${props => props.gameStarted ? 'flex' : 'none'};
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const StartMenu = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #000000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
`;

const StartTitle = styled.div`
  font-family: 'Press Start 2P', monospace;
  color: white;
  font-size: 24px;
  text-align: center;
  line-height: 1.5;
  margin-bottom: 40px;
  text-shadow: 3px 3px 0px #3f3f3f;
`;

const StartButton = styled.button`
  font-family: 'Press Start 2P', monospace;
  font-size: 20px;
  padding: 15px 30px;
  background: #727272;
  border: 4px solid;
  border-color: #ffffff #404040 #404040 #ffffff;
  color: white;
  text-shadow: 2px 2px 0px #000000;
  cursor: pointer;
  min-width: 200px;
  transition: all 0.1s;
  
  &:hover {
    background: #8f8f8f;
    transform: scale(1.05);
  }
  
  &:active {
    border-color: #404040 #ffffff #ffffff #404040;
    transform: scale(0.95);
  }
`;

const BoardContainer = styled.div`
  width: 400px;
  max-width: 100%;
  margin: 0 auto;
  border: 2px solid;
  border-color: #808080 #ffffff #ffffff #808080;
`;

const Controls = styled.div`
  margin-top: 20px;
  display: flex;
  gap: 10px;
`;

const Button = styled.button`
  padding: 8px 16px;
  background: #d4d0c8;
  border: 2px solid;
  border-color: #ffffff #808080 #808080 #ffffff;
  cursor: pointer;
  font-family: 'MS Sans Serif', sans-serif;
  
  &:active {
    border-color: #808080 #ffffff #ffffff #808080;
  }
`;

const StatusBar = styled.div`
  margin-top: 15px;
  padding: 8px;
  background: white;
  border: 2px inset #d4d0c8;
  width: 100%;
  text-align: center;
  font-family: 'MS Sans Serif', sans-serif;
`;

const MessageText = styled.div`
  font-family: 'MS Sans Serif', sans-serif;
  color: #000;
  margin-bottom: 15px;
  text-align: center;
  font-style: italic;
`;

// Custom piece renderer
const createPiece = (color, shape) => ({ squareWidth }) => (
  <div
    style={{
      width: squareWidth,
      height: squareWidth,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}
  >
    <svg width={squareWidth * 0.8} height={squareWidth * 0.8} viewBox="0 0 32 32">
      {/* Brown color scheme */}
      <g style={{ imageRendering: 'pixelated' }}>
        {color === 'w' ? (
          // Light pieces (tan/brown)
          <>
            {/* Base */}
            <rect x="8" y="24" width="16" height="4" fill="#D4B483" />
            {shape}
          </>
        ) : (
          // Dark pieces (darker brown)
          <>
            {/* Base */}
            <rect x="8" y="24" width="16" height="4" fill="#6B4423" />
            {shape}
          </>
        )}
      </g>
    </svg>
  </div>
);

const customSquareStyles = {
  light: { backgroundColor: '#EEEED2' },
  dark: { backgroundColor: '#769656' }
};

// Simple evaluation function
const evaluateBoard = (game) => {
  const pieceValues = {
    p: 1,
    n: 3,
    b: 3,
    r: 5,
    q: 9,
    k: 0
  };

  let score = 0;
  const board = game.board();

  for (let i = 0; i < 8; i++) {
    for (let j = 0; j < 8; j++) {
      const piece = board[i][j];
      if (piece) {
        const value = pieceValues[piece.type.toLowerCase()];
        score += piece.color === 'w' ? value : -value;
      }
    }
  }
  return score;
};

// Get best move for AI
const getBestMove = (game, depth = 3) => {
  const moves = game.moves({ verbose: true });
  let bestMove = null;
  let bestValue = -9999;

  for (let i = 0; i < moves.length; i++) {
    const move = moves[i];
    game.move(move);
    const value = -minimax(game, depth - 1, -10000, 10000, false);
    game.undo();

    if (value >= bestValue) {
      bestValue = value;
      bestMove = move;
    }
  }

  return bestMove;
};

// Minimax algorithm with alpha-beta pruning
const minimax = (game, depth, alpha, beta, isMaximizingPlayer) => {
  if (depth === 0) {
    return evaluateBoard(game);
  }

  const moves = game.moves({ verbose: true });

  if (isMaximizingPlayer) {
    let bestValue = -9999;
    for (const move of moves) {
      game.move(move);
      bestValue = Math.max(bestValue, minimax(game, depth - 1, alpha, beta, false));
      game.undo();
      alpha = Math.max(alpha, bestValue);
      if (beta <= alpha) {
        break;
      }
    }
    return bestValue;
  } else {
    let bestValue = 9999;
    for (const move of moves) {
      game.move(move);
      bestValue = Math.min(bestValue, minimax(game, depth - 1, alpha, beta, true));
      game.undo();
      beta = Math.min(beta, bestValue);
      if (beta <= alpha) {
        break;
      }
    }
    return bestValue;
  }
};

export const ChessGame = ({ 
  onClose, 
  isActive, 
  onActivate, 
  isMinimized, 
  isMaximized, 
  onMinimize, 
  onMaximize,
  initialPosition
}) => {
  const [game, setGame] = useState(new Chess());
  const [status, setStatus] = useState('Your turn');
  const [gameStarted, setGameStarted] = useState(false);

  const makeMove = (move) => {
    try {
      const result = game.move(move);
      setGame(new Chess(game.fen()));
      return result;
    } catch (e) {
      return null;
    }
  };

  const makeAIMove = () => {
    setStatus('Thinking...');
    // Use setTimeout to prevent UI freeze
    setTimeout(() => {
      const bestMove = getBestMove(game);
      if (bestMove) {
        makeMove(bestMove);
        setStatus('Your turn');
      } else {
        setStatus(game.isCheckmate() ? 'Checkmate!' : game.isDraw() ? 'Draw!' : 'Your turn');
      }
    }, 100);
  };

  const onDrop = (sourceSquare, targetSquare) => {
    if (game.turn() === 'b') return false; // Prevent moving during AI turn

    const move = makeMove({
      from: sourceSquare,
      to: targetSquare,
      promotion: 'q' // Always promote to queen for simplicity
    });

    if (move === null) return false;

    // AI's turn
    setTimeout(makeAIMove, 300);
    return true;
  };

  const resetGame = () => {
    setGame(new Chess());
    setStatus('Your turn');
  };

  return (
    <Window
      title="Chess"
      onClose={onClose}
      isActive={isActive}
      onActivate={onActivate}
      isMinimized={isMinimized}
      isMaximized={isMaximized}
      onMinimize={onMinimize}
      onMaximize={onMaximize}
      initialPosition={initialPosition}
      defaultWidth="500px"
      defaultHeight="550px"
    >
      <ChessContainer>
        {!gameStarted ? (
          <StartMenu>
            <StartTitle>prepare to get pwnd in chess</StartTitle>
            <StartButton onClick={() => setGameStarted(true)}>
              START
            </StartButton>
          </StartMenu>
        ) : (
          <GameContent gameStarted={gameStarted}>
            <MessageText>
              trying to create my own pieces was a bad idea, you'll figure it out
            </MessageText>
            <BoardContainer>
              <Chessboard
                position={game.fen()}
                onPieceDrop={onDrop}
                boardOrientation="white"
                customBoardStyle={{
                  borderRadius: '0',
                  boxShadow: 'none',
                }}
                customDarkSquareStyle={customSquareStyles.dark}
                customLightSquareStyle={customSquareStyles.light}
                customPieces={customPieces}
              />
            </BoardContainer>
            <Controls>
              <Button onClick={resetGame}>New Game</Button>
              <Button onClick={() => {
                game.undo();
                game.undo();
                setGame(new Chess(game.fen()));
              }}>Undo</Button>
            </Controls>
            <StatusBar>{status}</StatusBar>
          </GameContent>
        )}
      </ChessContainer>
    </Window>
  );
}; 